import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import axiosInstance from '../configs/axios';

// The first ten mirror `PromoCodeValidationMessageEnum` in the API; the last three are invented here
// from the HTTP status (401/429/transport), where the server never judged the code at all. This union
// is NOT exhaustive over the server's vocabulary and must not be treated as such — the API is free to
// add a case, and `message` below is whatever string came on the wire, asserted into this type. See
// `docs/promo-code-messages.md`.
//
// Declared as an array so the i18n guard can cross-check it against the locale files at runtime; a
// bare union is invisible to Jest.
export const PROMO_CODE_VALIDATION_MESSAGES = ['not_found', 'disabled', 'expired', 'exhausted', 'gateway_not_eligible', 'country_not_eligible', 'plan_not_eligible', 'per_customer_limit', 'recurring_unsupported_gateway', 'gateway_sync_pending', 'unauthenticated', 'rate_limited', 'network_error'] as const;

export type PromoCodeValidationMessage = (typeof PROMO_CODE_VALIDATION_MESSAGES)[number];

interface ValidationArgs {
  code: string;
  plan_id: number;
  gateway_id: number;
  country_code?: string;
}

interface ValidationResponse {
  valid: boolean;
  percentage?: number;
  applies_to_recurring?: boolean;
  duration?: 'once' | 'repeating' | 'forever';
  duration_in_cycles?: number | null;
  promo_code_id?: number | null;
  message?: PromoCodeValidationMessage;
}

interface RejectedPayload {
  valid: false;
  message: PromoCodeValidationMessage;
}

export const validatePromoCode = createAsyncThunk<ValidationResponse, ValidationArgs, { rejectValue: RejectedPayload }>('promoCodeDiscount/validate', async (args, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post<ValidationResponse>('/promo-codes/validate', args);
    return response.data;
  } catch (err: unknown) {
    const httpStatus = (err as { response?: { status?: number } })?.response?.status;
    if (httpStatus === 401) return rejectWithValue({ valid: false, message: 'unauthenticated' });
    if (httpStatus === 429) return rejectWithValue({ valid: false, message: 'rate_limited' });
    // 5xx, network failure, or anything else → transport error, not an invalid promo code.
    return rejectWithValue({ valid: false, message: 'network_error' });
  }
});

export interface PromoCodeDiscountState {
  status: 'idle' | 'loading' | 'success' | 'error';
  code: string | null;
  percentage: number | null;
  appliesToRecurring: boolean | null;
  message: PromoCodeValidationMessage | null;
  /** Code of the validation currently in flight, so a re-check of the applied code is told apart from a new one. */
  pendingCode: string | null;
}

const initialState: PromoCodeDiscountState = {
  status: 'idle',
  code: null,
  percentage: null,
  appliesToRecurring: null,
  message: null,
  pendingCode: null,
};

const promoCodeDiscountSlice = createSlice({
  name: 'promoCodeDiscount',
  initialState,
  reducers: {
    clearPromoCode(state) {
      Object.assign(state, initialState);
    },
    setPromoCode(state, action: PayloadAction<string>) {
      // eslint-disable-next-line no-param-reassign
      state.code = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(validatePromoCode.pending, (state, action) => {
        // eslint-disable-next-line no-param-reassign
        state.status = 'loading';
        // eslint-disable-next-line no-param-reassign
        state.message = null;
        // eslint-disable-next-line no-param-reassign
        state.pendingCode = action.meta.arg.code;
      })
      .addCase(validatePromoCode.fulfilled, (state, action) => {
        // eslint-disable-next-line no-param-reassign
        state.pendingCode = null;
        if (action.payload.valid) {
          // eslint-disable-next-line no-param-reassign
          state.status = 'success';
          // eslint-disable-next-line no-param-reassign
          state.code = action.meta.arg.code;
          // eslint-disable-next-line no-param-reassign
          state.percentage = action.payload.percentage ?? null;
          // eslint-disable-next-line no-param-reassign
          state.appliesToRecurring = action.payload.applies_to_recurring ?? null;
          // eslint-disable-next-line no-param-reassign
          state.message = null;
        } else {
          // eslint-disable-next-line no-param-reassign
          state.status = 'error';
          // eslint-disable-next-line no-param-reassign
          state.code = null;
          // eslint-disable-next-line no-param-reassign
          state.percentage = null;
          // eslint-disable-next-line no-param-reassign
          state.appliesToRecurring = null;
          // eslint-disable-next-line no-param-reassign
          state.message = action.payload.message ?? 'not_found';
        }
      })
      .addCase(validatePromoCode.rejected, (state, action) => {
        // eslint-disable-next-line no-param-reassign
        state.pendingCode = null;
        // eslint-disable-next-line no-param-reassign
        state.status = 'error';
        // eslint-disable-next-line no-param-reassign
        state.percentage = null;
        // eslint-disable-next-line no-param-reassign
        state.appliesToRecurring = null;
        // eslint-disable-next-line no-param-reassign
        state.message = action.payload?.message ?? 'network_error';
      });
  },
});

export const { clearPromoCode, setPromoCode } = promoCodeDiscountSlice.actions;
export default promoCodeDiscountSlice.reducer;
